import * as Sentry from '@sentry/react';
import { isSentryActive } from './sentry';

type ErrorContext = {
  op: string; // logical operation, e.g. 'save-coupon', 'fetch-offers', 'callPublicAPI'
  status?: number; // HTTP status, when the error originates from a request
  payload?: unknown; // scrubbed to an allowlist of diagnostic keys, then truncated to ~1KB
  extras?: Record<string, unknown>;
};

// Only these keys survive scrubbing of an object payload (e.g. an API error
// body). Everything else is dropped so we never ship PII (email, phone, token,
// consent, user-facing text) to Sentry. The list is intentionally non-PII
// diagnostic identifiers only — extend it deliberately. Applied at every depth,
// so nested `error`/`metadata` objects keep only their own allowlisted fields.
const PAYLOAD_ALLOWLIST = new Set([
  'code',
  'errorCode',
  'status',
  'statusCode',
  'error',
  'type',
  'description',
  'reason',
  'metadata',
  'trace',
  'timestamp'
]);

// Strings pass through (the human-readable error is already the captured
// exception message; a text body is not structured user data). Objects are
// reduced to PAYLOAD_ALLOWLIST keys recursively. Arrays and anything else are
// dropped — their contents are unbounded and may carry PII.
const scrubPayload = (value: unknown): unknown => {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || typeof value !== 'object' || Array.isArray(value)) {
    return undefined;
  }

  const out: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    if (!PAYLOAD_ALLOWLIST.has(key)) {
      continue;
    }
    if (val !== null && typeof val === 'object' && !Array.isArray(val)) {
      out[key] = scrubPayload(val);
    } else if (typeof val !== 'object') {
      out[key] = val;
    }
  }
  return out;
};

// Max one capture per `op` within this window. Prevents a storm of identical
// events during a transient outage.
const RATE_LIMIT_MS = 30_000;
const TRUNCATED_SUFFIX = '…[truncated]';
// Total truncated length stays within this cap, suffix included.
const PAYLOAD_MAX_CHARS = 1024;
// Reap expired entries once the map crosses this size so it cannot grow
// unbounded if a caller ever derives `op` from high-cardinality data.
const PRUNE_THRESHOLD = 100;

const lastReportByOp = new Map<string, number>();

const isRateLimited = (op: string): boolean => {
  const now = Date.now();
  const last = lastReportByOp.get(op);

  if (last !== undefined && now - last < RATE_LIMIT_MS) {
    return true;
  }

  if (lastReportByOp.size >= PRUNE_THRESHOLD) {
    let evictedExpired = false;
    for (const [key, ts] of lastReportByOp) {
      if (now - ts >= RATE_LIMIT_MS) {
        lastReportByOp.delete(key);
        evictedExpired = true;
      }
    }

    // Cap reached but nothing expired (only possible if a caller derives `op`
    // from high-cardinality data): evict the single oldest entry so the map
    // cannot grow unbounded.
    if (!evictedExpired) {
      let oldestKey: string | undefined;
      let oldestTs = Infinity;
      for (const [key, ts] of lastReportByOp) {
        if (ts < oldestTs) {
          oldestTs = ts;
          oldestKey = key;
        }
      }
      if (oldestKey !== undefined) {
        lastReportByOp.delete(oldestKey);
      }
    }
  }

  lastReportByOp.set(op, now);
  return false;
};

const truncatePayload = (payload: unknown): string => {
  let serialized: string;

  try {
    serialized =
      typeof payload === 'string'
        ? payload
        : (JSON.stringify(payload) ?? String(payload));
  } catch {
    serialized = String(payload);
  }

  if (serialized.length <= PAYLOAD_MAX_CHARS) {
    return serialized;
  }

  let end = PAYLOAD_MAX_CHARS - TRUNCATED_SUFFIX.length;
  // Don't slice through a surrogate pair — that would emit a lone surrogate
  // (replacement char) into the event.
  const lastKept = serialized.charCodeAt(end - 1);
  if (lastKept >= 0xd800 && lastKept <= 0xdbff) {
    end -= 1;
  }

  return serialized.slice(0, end) + TRUNCATED_SUFFIX;
};

/**
 * Reports an error to Sentry with structured operation context.
 *
 * Safe to call from anywhere: it is a no-op when Sentry has not been
 * initialized (dev / local builds) and rate-limits to one capture per `op`
 * every 30 seconds.
 */
export function reportError(err: unknown, ctx: ErrorContext): void {
  if (!isSentryActive()) {
    return;
  }

  if (isRateLimited(ctx.op)) {
    return;
  }

  const tags: Record<string, string | number> = { op: ctx.op };
  if (ctx.status !== undefined) {
    tags.status = ctx.status;
  }

  const extra: Record<string, unknown> = { ...ctx.extras };
  if (ctx.payload !== undefined) {
    const scrubbed = scrubPayload(ctx.payload);
    if (scrubbed !== undefined) {
      extra.payload = truncatePayload(scrubbed);
    }
  }

  Sentry.captureException(err, { tags, extra });
}
